import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  extractThumbnail,
  extractVideoUrl,
  extractTitle,
  extractDuration,
  extractIsNsfw,
  formatDuration,
  formatTimeAgo,
} from '@/hooks/useVideoFeed';
import { useVideoThumbnail } from '@/hooks/useVideoThumbnail';
import { isYouTubeUrl, getYouTubeThumbnailUrl, extractYouTubeVideoId } from '@/lib/youtube';
import { YouTubeIcon } from '@/components/YouTubeEmbed';

interface VideoCardProps {
  event: NostrEvent;
  horizontal?: boolean;
  compact?: boolean; // 250px width for trending carousel
  /** When provided a ⋮ menu appears on the card allowing the owner to delete it. */
  onDelete?: (event: NostrEvent) => void;
}


export function VideoCard({ event, horizontal = false, compact = false, onDelete }: VideoCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [nsfwRevealed, setNsfwRevealed] = useState(false);

  const title = extractTitle(event);
  const storedThumbnail = extractThumbnail(event);
  const videoUrl = extractVideoUrl(event);
  const duration = extractDuration(event);
  const isNsfw = extractIsNsfw(event);
  const timeAgo = formatTimeAgo(event.created_at);
  const displayName = metadata?.name ?? metadata?.display_name ?? event.pubkey.slice(0, 8) + '…';
  const avatar = metadata?.picture;

  // YouTube bridge: use YouTube's thumbnail CDN when no stored thumbnail exists
  const isYtVideo = videoUrl ? isYouTubeUrl(videoUrl) : false;
  const ytVideoId = isYtVideo && videoUrl ? extractYouTubeVideoId(videoUrl) : null;
  const ytFallbackThumb = ytVideoId ? getYouTubeThumbnailUrl(ytVideoId) : undefined;

  const thumbnail = useVideoThumbnail(event.id, storedThumbnail ?? ytFallbackThumb, videoUrl);

  const videoHref = `/video/${event.id}`;
  const authorHref = `/profile/${event.pubkey}`;

  /* ── Horizontal (sidebar related) ── */
  if (horizontal) {
    return (
      <div className="flex gap-3 group">
        <Link
          to={videoHref}
          className="relative shrink-0 w-40 sm:w-44"
          style={{ aspectRatio: '16/9', display: 'block' }}
        >
          <div
            className="w-full h-full rounded-xl overflow-hidden"
            style={{ background: 'var(--bg-secondary)' }}
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                style={isNsfw && !nsfwRevealed ? { filter: 'blur(12px)', transform: 'scale(1.05)' } : undefined}
              />
            ) : (
              <ShimmerThumb />
            )}
          </div>
          {/* NSFW overlay */}
          {isNsfw && !nsfwRevealed && (
            <div
              className="absolute inset-0 rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer"
              style={{ background: 'rgba(0,0,0,0.45)' }}
              onClick={e => { e.preventDefault(); setNsfwRevealed(true); }}
            >
              <span className="text-lg">🔞</span>
              <span className="text-white text-[10px] font-bold uppercase tracking-wide">NSFW</span>
            </div>
          )}
          {/* YouTube badge for horizontal card */}
          {isYtVideo && (
            <span
              className="absolute top-1 left-1 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded font-bold text-white"
              style={{ background: '#ff0000' }}
            >
              <YouTubeIcon size={9} />
              YT
            </span>
          )}
          {duration !== undefined && (
            <span
              className="absolute bottom-1 right-1 text-xs px-1.5 py-0.5 rounded text-white font-mono"
              style={{ background: 'rgba(0,0,0,0.8)' }}
            >
              {formatDuration(duration)}
            </span>
          )}
        </Link>
        <div className="flex flex-col gap-1 flex-1 min-w-0 py-1">
          <Link
            to={videoHref}
            className="text-sm font-medium leading-snug line-clamp-2 no-underline hover:opacity-80 transition-opacity"
            style={{ color: 'var(--text-primary)' }}
          >
            {title}
          </Link>
          <Link
            to={authorHref}
            className="text-xs no-underline hover:underline truncate"
            style={{ color: 'var(--text-secondary)' }}
          >
            {displayName}
          </Link>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{timeAgo}</p>
        </div>
      </div>
    );
  }

  /* ── Standard card (grid or compact carousel) ── */
  return (
    <>
      <div
        className="gb-video-card group relative"
        style={{ padding: '0.5rem', marginBottom: 0, width: compact ? 250 : undefined }}
      >
        {/* Thumbnail */}
        <Link
          to={videoHref}
          className="relative block rounded-xl overflow-hidden"
          style={{ paddingBottom: '56.25%', background: 'var(--bg-secondary)', display: 'block' }}
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              style={isNsfw && !nsfwRevealed ? { filter: 'blur(16px)', transform: 'scale(1.08)' } : undefined}
            />
          ) : (
            <div className="absolute inset-0">
              <ShimmerThumb />
            </div>
          )}
          {/* NSFW overlay — click reveals, then navigates on second click */}
          {isNsfw && !nsfwRevealed && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 cursor-pointer"
              style={{ background: 'rgba(0,0,0,0.5)' }}
              onClick={e => { e.preventDefault(); setNsfwRevealed(true); }}
            >
              <span className="text-3xl">🔞</span>
              <span className="text-white text-xs font-bold uppercase tracking-widest">Sensitive Content</span>
              <span className="text-white/70 text-[11px]">Tap to reveal</span>
            </div>
          )}
          {/* YouTube source badge */}
          {isYtVideo && (
            <span
              className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-bold text-white"
              style={{ background: '#ff0000' }}
            >
              <YouTubeIcon size={10} />
              YT
            </span>
          )}
          {/* Duration badge */}
          {duration !== undefined && (
            <span
              className="absolute bottom-2 right-2 text-xs px-1.5 py-0.5 rounded text-white font-mono"
              style={{ background: 'rgba(0,0,0,0.8)' }}
            >
              {formatDuration(duration)}
            </span>
          )}
          {/* Play overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.2)' }}
          >
            <div className="bg-white/90 rounded-full p-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="translate-x-0.5 text-black">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </Link>

        {/* Info row */}
        <div className="flex gap-2 pt-2">
          <Link to={authorHref} className="shrink-0">
            <Avatar className="h-9 w-9">
              <AvatarImage src={avatar} />
              <AvatarFallback
                className="text-xs font-bold"
                style={{ background: 'var(--bg-secondary)', color: 'var(--gb-accent)' }}
              >
                {displayName[0]?.toUpperCase() ?? '?'}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0">
            {/* Title */}
            <Link
              to={videoHref}
              className="block text-sm font-medium leading-snug no-underline hover:opacity-80 transition-opacity"
              style={{
                color: 'var(--text-primary)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              } as React.CSSProperties}
            >
              {title}
            </Link>
            {/* Author */}
            <Link
              to={authorHref}
              className="text-xs no-underline hover:underline mt-0.5 block truncate"
              style={{ color: 'var(--text-secondary)' }}
            >
              {displayName}
            </Link>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{timeAgo}</p>
          </div>

          {/* ⋮ owner menu */}
          {onDelete && (
            <div className="relative shrink-0 self-start mt-0.5">
              <button
                type="button"
                aria-label="Video options"
                onClick={e => { e.preventDefault(); setMenuOpen(v => !v); }}
                className="flex items-center justify-center w-7 h-7 rounded-full transition-colors hover:bg-black/10 dark:hover:bg-white/10"
                style={{ color: 'var(--text-secondary)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                </svg>
              </button>

              {menuOpen && (
                <>
                  {/* Backdrop to close on outside click */}
                  <div
                    className="fixed inset-0 z-[300]"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div
                    className="absolute right-0 top-8 z-[301] rounded-lg shadow-lg py-1 min-w-[130px]"
                    style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)' }}
                  >
                    <button
                      type="button"
                      onClick={e => { e.preventDefault(); setMenuOpen(false); setConfirmOpen(true); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-red-500/10"
                      style={{ color: '#f44336' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation dialog — outside the card so it renders in a portal */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent style={{ background: 'var(--bg-secondary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'var(--text-primary)' }}>Delete video?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'var(--text-secondary)' }}>
              This publishes a NIP-09 deletion request to all connected relays. The video will be
              removed from Goblinbox immediately. Note that some relays may retain a copy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: 'var(--bg-primary)', border: '1px solid var(--gb-border)', color: 'var(--text-primary)' }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(event)}
              style={{ background: '#f44336', color: '#fff', border: 'none' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Pulsing shimmer shown while a frame is being captured. */
function ShimmerThumb() {
  return (
    <div className="w-full h-full flex items-center justify-center animate-pulse" style={{ background: 'var(--bg-secondary)' }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-border)', opacity: 0.4 }}>
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  );
}

export function VideoCardSkeleton({ horizontal = false }: { horizontal?: boolean }) {
  if (horizontal) {
    return (
      <div className="flex gap-3">
        <Skeleton className="shrink-0 w-40 sm:w-44 rounded-xl" style={{ aspectRatio: '16/9' }} />
        <div className="flex flex-col gap-2 flex-1 py-1">
          <Skeleton className="h-4 w-full" style={{ background: 'var(--bg-secondary)' }} />
          <Skeleton className="h-3 w-24" style={{ background: 'var(--bg-secondary)' }} />
          <Skeleton className="h-3 w-16" style={{ background: 'var(--bg-secondary)' }} />
        </div>
      </div>
    );
  }
  return (
    <div style={{ padding: '0.5rem' }}>
      <Skeleton className="w-full rounded-xl" style={{ paddingBottom: '56.25%', background: 'var(--bg-secondary)' }} />
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-9 w-9 rounded-full shrink-0" style={{ background: 'var(--bg-secondary)' }} />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-full" style={{ background: 'var(--bg-secondary)' }} />
          <Skeleton className="h-3 w-1/2" style={{ background: 'var(--bg-secondary)' }} />
          <Skeleton className="h-3 w-1/3" style={{ background: 'var(--bg-secondary)' }} />
        </div>
      </div>
    </div>
  );
}
